import { CheckIcon } from '@heroicons/react/24/outline';

const tiers = [
    {
        name: 'Beta',
        id: 'tier-beta',
        href: '#',
        priceMonthly: '$0',
        description: 'Our free plan you can play and have fun with.',
        features: [
            'Free to use',
            'Access to Romr (Beta) and Clickerbooth',
            'Photo Engineers branded',
            'Text, Email, and QR sharing',
            'One device at a time',
        ],
        featured: false,
    },
    {
        name: 'Alpha',
        id: 'tier-alpha',
        href: '#',
        priceMonthly: '$49',
        description: "Let's get customizing.",
        features: [
            'Access to Romr (Beta) and Clickerbooth',
            'Customize artwork',
            'Customize sharing',
            'Customize event templates',
            'Share stat downloads',
            'Device management',
            'Text, Email, and QR sharing',
            'One simultaneous device per license',
        ],
        featured: true,
        comingSoon: true,
    },
];

function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ');
}

export default function Plans() {
    return (
        <div className="relative isolate px-6 py-8 sm:py-32 lg:px-8">
            <div
                aria-hidden="true"
                className="absolute inset-x-0 -top-3 -z-10 transform-gpu overflow-hidden px-36 blur-3xl"
            ></div>
            <div className="mx-auto max-w-4xl text-center">
                <h2 className="text-base/7 font-semibold text-zinc-600 dark:text-gray-200">
                    Plans & Pricing
                </h2>
                <p className="mt-2 text-5xl plex-sans-semibold tracking-tight text-balance text-[#111827] sm:text-6xl dark:text-gray-200">
                    Choose the right plan for you
                </p>
            </div>
            <p className="mx-auto mt-6 max-w-2xl text-center plex-sans text-lg font-medium text-pretty text-[#111827] sm:text-xl/8 dark:text-gray-200">
                Let's get weird together. Look over which features are important
                to you, and choose the plan that fits your needs.
            </p>
            <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 items-center gap-y-6 sm:mt-20 sm:gap-y-0 lg:max-w-6xl lg:grid-cols-2">
                {tiers.map((tier, tierIdx) => (
                    <div
                        key={tier.id}
                        className={classNames(
                            tier.featured
                                ? 'relative bg-[#262C2C] shadow-2xl'
                                : 'bg-white/90 sm:mx-8 lg:mx-0',
                            tier.featured
                                ? ''
                                : tierIdx === 0
                                ? 'rounded-t-2xl sm:rounded-b-none lg:rounded-tr-none lg:rounded-bl-2xl'
                                : 'sm:rounded-t-none lg:rounded-tr-2xl lg:rounded-bl-none',
                            'rounded-2xl p-8 ring-1 ring-gray-900/10 sm:p-10',
                        )}
                    >
                        <h3
                            id={tier.id}
                            className={classNames(
                                tier.featured
                                    ? 'text-zinc-400'
                                    : 'text-zinc-600',
                                'text-base/7 font-semibold monospace-font text-2xl',
                            )}
                        >
                            {tier.name}
                        </h3>
                        <p className="mt-4 flex items-baseline gap-x-2">
                            <span
                                className={classNames(
                                    tier.featured
                                        ? 'text-white'
                                        : 'text-gray-900',
                                    'text-5xl font-semibold tracking-tight monospace-font',
                                )}
                            >
                                {tier.priceMonthly}
                            </span>
                            <span
                                className={classNames(
                                    tier.featured
                                        ? 'text-gray-400'
                                        : 'text-gray-500',
                                    'text-base',
                                )}
                            >
                                /month
                            </span>
                        </p>
                        <p
                            className={classNames(
                                tier.featured
                                    ? 'text-gray-300'
                                    : 'text-gray-600',
                                'mt-6 plex-sans',
                            )}
                        >
                            {tier.description}
                        </p>
                        <ul
                            className={classNames(
                                tier.featured
                                    ? 'text-gray-300'
                                    : 'text-gray-600',
                                'mt-8 space-y-3 text-sm/6 sm:mt-10',
                            )}
                        >
                            {tier.features.map((feature) => (
                                <li
                                    key={feature}
                                    className="flex gap-x-3 monospace-font text-md"
                                >
                                    <CheckIcon
                                        aria-hidden="true"
                                        className={classNames(
                                            tier.featured
                                                ? 'text-zinc-400'
                                                : 'text-zinc-600',
                                            'h-6 w-5 flex-none',
                                        )}
                                    />
                                    {feature}
                                </li>
                            ))}
                        </ul>
                        {/* eslint-disable-next-line  */}
                        <a
                            aria-describedby={tier.id}
                            className={classNames(
                                tier.featured
                                    ? 'bg-zinc-500 text-white shadow-xs hover:bg-zinc-400 focus-visible:outline-zinc-500'
                                    : 'text-zinc-600 ring-1 ring-zinc-200 ring-inset hover:ring-zinc-300 focus-visible:outline-zinc-600',
                                'text-md mt-8 block rounded-md px-3.5 py-2.5 text-center font-semibold focus-visible:outline-2 focus-visible:outline-offset-2 sm:mt-10',
                            )}
                        >
                            {tier.comingSoon
                                ? 'Coming Soon'
                                : 'Your current plan'}
                        </a>
                    </div>
                ))}
            </div>
        </div>
    );
}
